import React, { useState } from 'react';
import {
  TrendingUp,
  Users,
  DollarSign,
  Instagram,
  Twitter,
  Star,
  Zap,
  BarChart3,
  Target,
  Award,
  ChevronUp,
  ChevronDown,
  Sparkles
} from 'lucide-react';

/**
 * NIL Player Card - Premium athlete card inspired by On3, Opendorse, NILSU
 * Features mobile phone frame, stats/NIL toggle, modern adtech design
 */

// Animated gradient background
const GradientBackground = ({ color1 = '#E8B800', color2 = '#FF6B00' }) => (
  <div
    className="absolute inset-0 opacity-40"
    style={{
      background: `linear-gradient(135deg, ${color1} 0%, ${color2} 50%, #000 100%)`
    }}
  />
);

// Social media stat pill
const SocialPill = ({ icon: Icon, count, platform, color }) => (
  <div className="flex items-center gap-1.5 px-2 py-1 rounded-full bg-black/40 backdrop-blur-sm border border-white/10">
    <Icon className="w-3 h-3" style={{ color }} />
    <span className="text-xs font-bold text-white">{count}</span>
  </div>
);

// Stat bar with animation
const StatBar = ({ label, value, max = 100, color = '#E8B800' }) => {
  const percentage = Math.min((value / max) * 100, 100);
  return (
    <div className="space-y-1">
      <div className="flex justify-between items-center">
        <span className="text-xs text-gray-400 uppercase tracking-wider">{label}</span>
        <span className="text-sm font-bold text-white">{value}</span>
      </div>
      <div className="h-1.5 bg-white/10 rounded-full overflow-hidden">
        <div
          className="h-full rounded-full transition-all duration-1000 ease-out"
          style={{
            width: `${percentage}%`,
            background: `linear-gradient(90deg, ${color}, ${color}88)`
          }}
        />
      </div>
    </div>
  );
};

// NIL Value display with breakdown
const NILValueDisplay = ({ totalValue, rosterValue, brandValue, trend }) => (
  <div className="space-y-3">
    <div className="text-center">
      <p className="text-xs text-gray-400 uppercase tracking-wider mb-1">NIL Valuation</p>
      <div className="flex items-center justify-center gap-2">
        <span className="text-3xl font-black text-white">${totalValue}</span>
        <div className={`flex items-center gap-0.5 px-1.5 py-0.5 rounded text-xs font-bold ${
          trend >= 0 ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'
        }`}>
          {trend >= 0 ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
          {Math.abs(trend)}%
        </div>
      </div>
    </div>

    <div className="grid grid-cols-2 gap-2">
      <div className="bg-white/5 rounded-lg p-2 border border-white/10">
        <p className="text-[10px] text-gray-500 uppercase">Roster Value</p>
        <p className="text-sm font-bold text-yellow-400">${rosterValue}</p>
      </div>
      <div className="bg-white/5 rounded-lg p-2 border border-white/10">
        <p className="text-[10px] text-gray-500 uppercase">Brand Value</p>
        <p className="text-sm font-bold text-orange-400">${brandValue}</p>
      </div>
    </div>
  </div>
);

// Performance rating circle
const RatingCircle = ({ rating, label }) => (
  <div className="relative w-16 h-16">
    <svg className="w-full h-full transform -rotate-90">
      <circle
        cx="32"
        cy="32"
        r="28"
        fill="none"
        stroke="rgba(255,255,255,0.1)"
        strokeWidth="4"
      />
      <circle
        cx="32"
        cy="32"
        r="28"
        fill="none"
        stroke="url(#gradient)"
        strokeWidth="4"
        strokeLinecap="round"
        strokeDasharray={`${rating * 1.76} 176`}
        className="transition-all duration-1000 ease-out"
      />
      <defs>
        <linearGradient id="gradient" x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="#E8B800" />
          <stop offset="100%" stopColor="#FF6B00" />
        </linearGradient>
      </defs>
    </svg>
    <div className="absolute inset-0 flex flex-col items-center justify-center">
      <span className="text-lg font-black text-white">{rating}</span>
      <span className="text-[8px] text-gray-400 uppercase">{label}</span>
    </div>
  </div>
);

// Stats Overlay Content
const StatsOverlay = ({ player }) => (
  <div className="space-y-4">
    {/* Core Stats */}
    <div className="grid grid-cols-3 gap-2">
      <div className="bg-white/5 rounded-lg p-2 text-center border border-white/10">
        <p className="text-lg font-black text-white">{player.ppg || '0.0'}</p>
        <p className="text-[10px] text-gray-400 uppercase">PPG</p>
      </div>
      <div className="bg-white/5 rounded-lg p-2 text-center border border-white/10">
        <p className="text-lg font-black text-white">{player.rpg || '0.0'}</p>
        <p className="text-[10px] text-gray-400 uppercase">RPG</p>
      </div>
      <div className="bg-white/5 rounded-lg p-2 text-center border border-white/10">
        <p className="text-lg font-black text-white">{player.apg || '0.0'}</p>
        <p className="text-[10px] text-gray-400 uppercase">APG</p>
      </div>
    </div>

    {/* Performance Bars */}
    <div className="space-y-2">
      <StatBar label="Shooting" value={player.shootingRating || 75} color="#E8B800" />
      <StatBar label="Athleticism" value={player.athleticismRating || 82} color="#FF6B00" />
      <StatBar label="Basketball IQ" value={player.bbiqRating || 78} color="#00D4FF" />
      <StatBar label="Defense" value={player.defenseRating || 70} color="#00FF88" />
    </div>

    {/* Ratings */}
    <div className="flex justify-center gap-4 pt-2">
      <RatingCircle rating={player.overallRating || 85} label="Overall" />
      <RatingCircle rating={player.potentialRating || 92} label="Potential" />
    </div>
  </div>
);

// NIL Overlay Content
const NILOverlay = ({ player }) => (
  <div className="space-y-4">
    {/* NIL Value */}
    <NILValueDisplay
      totalValue={player.nilValue || '125K'}
      rosterValue={player.rosterValue || '85K'}
      brandValue={player.brandValue || '40K'}
      trend={player.nilTrend || 12}
    />

    {/* Social Media */}
    <div className="space-y-2">
      <p className="text-xs text-gray-400 uppercase tracking-wider">Social Reach</p>
      <div className="grid grid-cols-2 gap-2">
        <div className="flex items-center gap-2 bg-white/5 rounded-lg p-2 border border-white/10">
          <Instagram className="w-4 h-4 text-pink-400" />
          <div>
            <p className="text-sm font-bold text-white">{player.instagram || '24.5K'}</p>
            <p className="text-[10px] text-gray-500">Followers</p>
          </div>
        </div>
        <div className="flex items-center gap-2 bg-white/5 rounded-lg p-2 border border-white/10">
          <Twitter className="w-4 h-4 text-blue-400" />
          <div>
            <p className="text-sm font-bold text-white">{player.twitter || '8.2K'}</p>
            <p className="text-[10px] text-gray-500">Followers</p>
          </div>
        </div>
      </div>
    </div>

    {/* PIE Scores */}
    <div className="space-y-2">
      <p className="text-xs text-gray-400 uppercase tracking-wider">P.I.E. Score</p>
      <StatBar label="Performance" value={player.performanceScore || 88} color="#E8B800" />
      <StatBar label="Influence" value={player.influenceScore || 72} color="#FF6B00" />
      <StatBar label="Exposure" value={player.exposureScore || 65} color="#00D4FF" />
    </div>

    {/* Deal Activity */}
    <div className="bg-gradient-to-r from-yellow-500/10 to-orange-500/10 rounded-lg p-3 border border-yellow-500/20">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-yellow-400" />
          <span className="text-xs text-gray-300">Active Deals</span>
        </div>
        <span className="text-sm font-bold text-yellow-400">{player.activeDeals || 3}</span>
      </div>
    </div>
  </div>
);

// Mobile Phone Frame
const PhoneFrame = ({ children }) => (
  <div className="relative mx-auto" style={{ width: '320px' }}>
    {/* Phone outer frame */}
    <div className="relative bg-[#141414] rounded-[3rem] p-3 shadow-2xl border border-gray-700">
      {/* Notch */}
      <div className="absolute top-0 left-1/2 transform -translate-x-1/2 w-32 h-7 bg-[#141414] rounded-b-2xl z-20 flex items-center justify-center">
        <div className="w-16 h-4 bg-black rounded-full" />
      </div>

      {/* Screen */}
      <div className="relative bg-black rounded-[2.5rem] overflow-hidden">
        {/* Status bar */}
        <div className="absolute top-0 left-0 right-0 h-8 bg-black/50 backdrop-blur-sm z-10 flex items-center justify-between px-8 pt-1">
          <span className="text-[10px] text-white font-medium">9:41</span>
          <div className="flex items-center gap-1">
            <div className="w-4 h-2 border border-white rounded-sm">
              <div className="w-2/3 h-full bg-green-400 rounded-sm" />
            </div>
          </div>
        </div>

        {/* Content */}
        {children}

        {/* Home indicator */}
        <div className="absolute bottom-2 left-1/2 transform -translate-x-1/2 w-32 h-1 bg-white/30 rounded-full" />
      </div>
    </div>
  </div>
);

// Main NIL Player Card Component
const NILPlayerCard = ({
  player = {},
  showPhoneFrame = true,
  onClose
}) => {
  const [activeOverlay, setActiveOverlay] = useState('stats'); // 'stats' | 'nil'

  // Default player data
  const defaultPlayer = {
    name: player.name || player.Player || 'Marcus Thompson',
    position: player.position || player.Position || 'PG',
    team: player.team || 'Nevada Wolf Pack',
    number: player.number || '#23',
    class: player.class || player.year || 'Junior',
    height: player.height || player.Height || "6'2\"",
    weight: player.weight || player.Weight || '185 lbs',
    image: player.image || player.photo || null,
    // Stats
    ppg: player.ppg || '18.5',
    rpg: player.rpg || '4.2',
    apg: player.apg || '6.8',
    shootingRating: player.shootingRating || 82,
    athleticismRating: player.athleticismRating || 78,
    bbiqRating: player.bbiqRating || 85,
    defenseRating: player.defenseRating || 72,
    overallRating: player.overallRating || 84,
    potentialRating: player.potentialRating || 91,
    // NIL
    nilValue: player.nilValue || '125K',
    rosterValue: player.rosterValue || '85K',
    brandValue: player.brandValue || '40K',
    nilTrend: player.nilTrend || 12,
    instagram: player.instagram || '24.5K',
    twitter: player.twitter || '8.2K',
    performanceScore: player.performanceScore || 88,
    influenceScore: player.influenceScore || 72,
    exposureScore: player.exposureScore || 65,
    activeDeals: player.activeDeals || 3
  };

  const CardContent = () => (
    <div className="relative h-[600px] bg-black overflow-hidden">
      {/* Background gradient */}
      <GradientBackground color1="#E8B800" color2="#FF6B00" />

      {/* Player image area */}
      <div className="relative h-48 overflow-hidden">
        {defaultPlayer.image ? (
          <img
            src={defaultPlayer.image}
            alt={defaultPlayer.name}
            className="w-full h-full object-cover object-top"
          />
        ) : (
          <div className="w-full h-full bg-gradient-to-b from-yellow-500/20 to-transparent flex items-center justify-center">
            <div className="w-24 h-24 rounded-full bg-gradient-to-br from-yellow-400 to-orange-500 flex items-center justify-center text-black font-black text-3xl">
              {defaultPlayer.name.split(' ').map(n => n[0]).join('')}
            </div>
          </div>
        )}

        {/* Gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent" />

        {/* Social pills */}
        <div className="absolute top-12 right-3 flex flex-col gap-1">
          <SocialPill icon={Instagram} count={defaultPlayer.instagram} platform="instagram" color="#E1306C" />
          <SocialPill icon={Twitter} count={defaultPlayer.twitter} platform="twitter" color="#1DA1F2" />
        </div>

        {/* Rating badge */}
        <div className="absolute top-12 left-3 bg-gradient-to-r from-yellow-500 to-orange-500 rounded-lg px-2 py-1">
          <div className="flex items-center gap-1">
            <Star className="w-3 h-3 text-black fill-current" />
            <span className="text-xs font-black text-black">{defaultPlayer.overallRating}</span>
          </div>
        </div>

        {/* Player info */}
        <div className="absolute bottom-3 left-3 right-3">
          <div className="flex items-end justify-between">
            <div>
              <p className="text-yellow-400 text-xs font-bold uppercase tracking-wider">{defaultPlayer.position} • {defaultPlayer.number}</p>
              <h2 className="text-2xl font-black text-white leading-tight">{defaultPlayer.name}</h2>
              <p className="text-gray-400 text-sm">{defaultPlayer.team}</p>
            </div>
            <div className="text-right">
              <p className="text-xs text-gray-400">{defaultPlayer.class}</p>
              <p className="text-xs text-gray-400">{defaultPlayer.height} • {defaultPlayer.weight}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Toggle buttons */}
      <div className="px-3 py-2">
        <div className="flex bg-white/5 rounded-xl p-1 border border-white/10">
          <button
            onClick={() => setActiveOverlay('stats')}
            className={`flex-1 flex items-center justify-center gap-2 py-2 rounded-lg text-sm font-bold transition-all ${
              activeOverlay === 'stats'
                ? 'bg-gradient-to-r from-yellow-500 to-orange-500 text-black'
                : 'text-gray-400 hover:text-white'
            }`}
          >
            <BarChart3 className="w-4 h-4" />
            Stats
          </button>
          <button
            onClick={() => setActiveOverlay('nil')}
            className={`flex-1 flex items-center justify-center gap-2 py-2 rounded-lg text-sm font-bold transition-all ${
              activeOverlay === 'nil'
                ? 'bg-gradient-to-r from-yellow-500 to-orange-500 text-black'
                : 'text-gray-400 hover:text-white'
            }`}
          >
            <DollarSign className="w-4 h-4" />
            NIL Value
          </button>
        </div>
      </div>

      {/* Overlay content */}
      <div className="px-3 pb-8 overflow-y-auto" style={{ maxHeight: '340px' }}>
        {activeOverlay === 'stats' ? (
          <StatsOverlay player={defaultPlayer} />
        ) : (
          <NILOverlay player={defaultPlayer} />
        )}
      </div>

      {/* Bottom action */}
      <div className="absolute bottom-6 left-3 right-3">
        <button className="w-full py-3 bg-gradient-to-r from-yellow-500 to-orange-500 rounded-xl text-black font-bold text-sm flex items-center justify-center gap-2 hover:from-yellow-400 hover:to-orange-400 transition-all shadow-lg shadow-orange-500/25">
          <Zap className="w-4 h-4" />
          Connect for NIL Deal
        </button>
      </div>
    </div>
  );

  if (showPhoneFrame) {
    return (
      <div className="p-6">
        <PhoneFrame>
          <CardContent />
        </PhoneFrame>
      </div>
    );
  }

  return <CardContent />;
};

export default NILPlayerCard;

// Export individual components for flexibility
export { PhoneFrame, StatsOverlay, NILOverlay, NILValueDisplay, RatingCircle, StatBar };
